import React, { useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Space, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ExpV2Layer } from '@/types/experimentsV2';
import { HOLDOUT_LAYER, LAYER_KEY_PATTERN } from './draftForm';
import { layerSegments } from './display';
import { bucketsToPercent, formatPercent, percentToBuckets } from './units';

/** Holdout size cap of the admin API: 200 of 1000 buckets. */
const HOLDOUT_MAX_PERCENT = 20;

interface Props {
  layers: ExpV2Layer[];
  canCreate: boolean;
  canEdit: boolean;
  onCreate: (values: { key: string; name: string; description?: string }) => Promise<void>;
  onHoldout: (buckets: number) => Promise<void>;
}

const SEGMENT_COLOR: Record<string, string> = {
  running: '#2f8f5b',
  scheduled: '#3b6fd1',
  paused: '#d48806',
  rolled_out: '#8a4fd1',
};

const BucketBar: React.FC<{ layer: ExpV2Layer }> = ({ layer }) => (
  <div
    style={{
      display: 'flex',
      height: 14,
      width: '100%',
      minWidth: 200,
      borderRadius: 3,
      overflow: 'hidden',
      background: 'rgba(127,127,127,0.15)',
    }}
  >
    {layerSegments(layer).map((s) => (
      <Tooltip
        key={`${s.start}-${s.key}`}
        title={
          s.status === 'free'
            ? `Free: buckets ${s.start}-${s.start + s.size - 1}`
            : `${s.key} (${s.status}): buckets ${s.start}-${s.start + s.size - 1}, live ${formatPercent(s.live)}`
        }
      >
        <div
          data-segment={s.status}
          style={{ width: `${s.size / 10}%`, display: 'flex', height: '100%' }}
        >
          {s.status !== 'free' && (
            <>
              <div
                style={{
                  width: `${(s.live / s.size) * 100}%`,
                  background: SEGMENT_COLOR[s.status] ?? '#888',
                }}
              />
              <div
                style={{ flex: 1, background: SEGMENT_COLOR[s.status] ?? '#888', opacity: 0.35 }}
              />
            </>
          )}
        </div>
      </Tooltip>
    ))}
  </div>
);

export const LayersPanel: React.FC<Props> = ({
  layers,
  canCreate,
  canEdit,
  onCreate,
  onHoldout,
}) => {
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [holdoutOpen, setHoldoutOpen] = useState(false);
  const [holdoutPercent, setHoldoutPercent] = useState<number | null>(null);
  const [form] = Form.useForm<{ key: string; name: string; description?: string }>();

  const holdout = layers.find((l) => l.key === HOLDOUT_LAYER);
  const holdoutBuckets = holdoutPercent === null ? null : percentToBuckets(holdoutPercent);
  const holdoutProblem =
    holdoutPercent === null
      ? 'Enter a size'
      : holdoutBuckets === null || holdoutPercent > HOLDOUT_MAX_PERCENT
        ? `Use 0% to ${HOLDOUT_MAX_PERCENT}% in steps of 0.1%`
        : null;

  const columns: ColumnsType<ExpV2Layer> = [
    {
      title: 'Layer',
      dataIndex: 'key',
      render: (key: string, l) => (
        <span data-layer={key}>
          <Typography.Text code>{key}</Typography.Text> {l.name}
        </span>
      ),
    },
    {
      title: 'Salt',
      dataIndex: 'salt',
      render: (salt: string) => (
        <Typography.Text copyable type="secondary">
          {salt}
        </Typography.Text>
      ),
    },
    {
      title: 'Buckets',
      key: 'bar',
      render: (_: unknown, l) =>
        l.key === HOLDOUT_LAYER ? (
          <Space>
            <span>Holdout {formatPercent(l.holdoutSize)}</span>
            <Typography.Text type="secondary">
              Holdout players do not get rolled-out winners.
            </Typography.Text>
            {canEdit && (
              <Button
                size="small"
                onClick={() => {
                  setHoldoutPercent(bucketsToPercent(l.holdoutSize));
                  setHoldoutOpen(true);
                }}
              >
                Change holdout
              </Button>
            )}
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={2}>
            <BucketBar layer={l} />
            <Typography.Text type="secondary">{formatPercent(l.usedBuckets)} used</Typography.Text>
          </Space>
        ),
    },
  ];

  const saveLayer = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await onCreate({
        key: values.key,
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
      });
      setCreating(false);
      form.resetFields();
    } finally {
      setSaving(false);
    }
  };

  const confirmHoldout = () => {
    if (holdoutBuckets === null || holdoutProblem) return;
    Modal.confirm({
      title: `Set the holdout to ${formatPercent(holdoutBuckets)}?`,
      content:
        'Holdout players stop getting rolled-out winners from the next request; running tests are not affected.',
      okText: 'Set holdout',
      onOk: async () => {
        await onHoldout(holdoutBuckets);
        setHoldoutOpen(false);
      },
    });
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {canCreate && (
        <Button type="primary" onClick={() => setCreating(true)}>
          Create layer
        </Button>
      )}
      <Table<ExpV2Layer>
        rowKey="key"
        columns={columns}
        dataSource={layers}
        pagination={false}
        scroll={{ x: true }}
      />

      <Modal
        title="Create layer"
        open={creating}
        okText="Create"
        confirmLoading={saving}
        onOk={saveLayer}
        onCancel={() => setCreating(false)}
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="key"
            label="Key"
            extra="Fixed once created. A salt is generated and never changes."
            rules={[
              { required: true, message: 'Enter a key' },
              {
                pattern: LAYER_KEY_PATTERN,
                message: 'Start with a-z; use a-z, 0-9 and _ (30 at most)',
              },
            ]}
          >
            <Input id="expv2-layer-key" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, whitespace: true, max: 100, message: 'Enter a name (100 at most)' },
            ]}
          >
            <Input id="expv2-layer-name" />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ max: 1000 }]}>
            <Input.TextArea id="expv2-layer-description" rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Holdout size"
        open={holdoutOpen && Boolean(holdout)}
        okText="Next"
        okButtonProps={{ disabled: Boolean(holdoutProblem) }}
        onOk={confirmHoldout}
        onCancel={() => setHoldoutOpen(false)}
      >
        <Space direction="vertical">
          <InputNumber
            id="expv2-holdout-percent"
            min={0}
            max={HOLDOUT_MAX_PERCENT}
            step={0.1}
            addonAfter="%"
            value={holdoutPercent}
            onChange={(v) => setHoldoutPercent(v)}
          />
          {holdoutProblem && <Typography.Text type="danger">{holdoutProblem}</Typography.Text>}
        </Space>
      </Modal>
    </Space>
  );
};
